import React, { useMemo, useState } from 'react'
import ArrowLeftIcon from "../../assets/icon/ArrowLeftIcon";
import ArrowRightIcon from "../../assets/icon/ArrowRightIcon";
import styles from './index.module.scss'
import { DatePicker } from "../../utils/DatePicker";

interface DatePickerProps {
    getDate: (date: Date) => void
    initialDate?: Date | null,
}

type CalendarMode = 'days' | 'months' | 'years'

const MONTH_NAMES = [
    '\u042f\u043d\u0432\u0430\u0440\u044c',
    '\u0424\u0435\u0432\u0440\u0430\u043b\u044c',
    '\u041c\u0430\u0440\u0442',
    '\u0410\u043f\u0440\u0435\u043b\u044c',
    '\u041c\u0430\u0439',
    '\u0418\u044e\u043d\u044c',
    '\u0418\u044e\u043b\u044c',
    '\u0410\u0432\u0433\u0443\u0441\u0442',
    '\u0421\u0435\u043d\u0442\u044f\u0431\u0440\u044c',
    '\u041e\u043a\u0442\u044f\u0431\u0440\u044c',
    '\u041d\u043e\u044f\u0431\u0440\u044c',
    '\u0414\u0435\u043a\u0430\u0431\u0440\u044c',
]

const WEEK_DAYS = ['\u043f\u043d', '\u0432\u0442', '\u0441\u0440', '\u0447\u0442', '\u043f\u0442', '\u0441\u0431', '\u0432\u0441']
const YEARS_GRID_SIZE = 12

export default function DatePickerComponents({ getDate, initialDate }: DatePickerProps) {
    const now = new Date()
    const initialCalendarDate = initialDate || now
    const [date, setDate] = useState<Date | null>(initialDate || null)
    const [mode, setMode] = useState<CalendarMode>('days')
    const [currentMonth, setCurrentMoth] = useState<number>(initialCalendarDate.getMonth() + 1)
    const [currentYear, setCurrentYear] = useState<number>(initialCalendarDate.getFullYear())
    const firstDay = new Date(currentYear, currentMonth - 1, 1).getDay()
    const adjustedFirstDay = firstDay === 0 ? 7 : firstDay
    const daysInMonth = DatePicker.getDaysCountInMonth(currentMonth, currentYear)
    const countOfUIElement = Math.ceil((adjustedFirstDay - 1 + daysInMonth) / 7) * 7
    const decadeStart = Math.floor(currentYear / 10) * 10
    const years = useMemo(() => (
        new Array(YEARS_GRID_SIZE).fill(null).map((_, index) => decadeStart - 1 + index)
    ), [decadeStart])

    const changeMonth = (newMonth: number) => {
        if (newMonth === 0) {
            setCurrentMoth(12)
            setCurrentYear(currentYear - 1)
            return
        }

        if (newMonth === 13) {
            setCurrentMoth(1)
            setCurrentYear(currentYear + 1)
            return
        }

        setCurrentMoth(newMonth)
    }

    const handlePrev = () => {
        if (mode === 'days') {
            changeMonth(currentMonth - 1)
            return
        }

        if (mode === 'months') {
            setCurrentYear(currentYear - 1)
            return
        }

        setCurrentYear(currentYear - 10)
    }

    const handleNext = () => {
        if (mode === 'days') {
            changeMonth(currentMonth + 1)
            return
        }

        if (mode === 'months') {
            setCurrentYear(currentYear + 1)
            return
        }

        setCurrentYear(currentYear + 10)
    }

    const setNewDate = (day: number, month: number, year: number) => {
        const nextDate = DatePicker.createDate(day, month, year)

        setCurrentMoth(month)
        setCurrentYear(year)
        setDate(nextDate)
        getDate(nextDate)
    }

    const selectMonth = (monthIndex: number) => {
        setCurrentMoth(monthIndex + 1)
        setMode('days')
    }

    const selectYear = (year: number) => {
        setCurrentYear(year)
        setMode('months')
    }

    const renderDays = () => (
        <div>
            <div className={styles.datePicker__month}>
                {WEEK_DAYS.map((weekDay) => (
                    <div key={weekDay} className={styles.datePicker__item}>{weekDay}</div>
                ))}
            </div>
            <div className={styles.datePicker__days}>
                {new Array(countOfUIElement).fill(null).map((_, index) => {
                    const increaseIndex = index + 1
                    const currentDay = increaseIndex - adjustedFirstDay + 1

                    if (currentDay <= 0) {
                        const prevMonthIndex = currentMonth - 1 === 0 ? 12 : currentMonth - 1
                        const prevMonthYear = prevMonthIndex === 12 ? currentYear - 1 : currentYear
                        const adjustedDaysCountInPrevMonth = DatePicker.getDaysCountInMonth(prevMonthIndex, prevMonthYear)
                        const daysInPrevMonthInCurrentMonthUI = adjustedFirstDay - 1
                        const currentPrevMonthDay = adjustedDaysCountInPrevMonth - daysInPrevMonthInCurrentMonthUI + increaseIndex

                        return (
                            <div key={`prev-${currentPrevMonthDay}`} className={styles.datePicker__item} onClick={() => setNewDate(currentPrevMonthDay, prevMonthIndex, prevMonthYear)}>
                                <div className={`${styles.datePicker__item_elem} ${styles.notCurrentDay}`}>
                                    {currentPrevMonthDay}
                                </div>
                            </div>
                        )
                    }

                    if (currentDay <= daysInMonth) {
                        const cellDate = new Date(currentYear, currentMonth - 1, currentDay)
                        const equalDate = date && DatePicker.dateEqual(DatePicker.dateMidnight(date), DatePicker.dateMidnight(cellDate))
                        const dateNow = DatePicker.dateEqual(DatePicker.dateMidnight(new Date()), DatePicker.dateMidnight(cellDate))
                        const weekday = cellDate.getDay()
                        const isWeekend = weekday === 0 || weekday === 6

                        return (
                            <div key={`current-${currentDay}`} className={styles.datePicker__item} onClick={() => setNewDate(currentDay, currentMonth, currentYear)}>
                                <div className={`${styles.datePicker__item_elem} ${isWeekend ? styles.weekend : ''} ${equalDate ? styles.active : ''} ${dateNow ? styles.now : ''}`}>
                                    {currentDay}
                                </div>
                            </div>
                        )
                    }

                    const nextMonthIndex = currentMonth + 1 === 13 ? 1 : currentMonth + 1
                    const nextMonthYear = nextMonthIndex === 1 ? currentYear + 1 : currentYear
                    const daysInNextMonthInCurrentMonthUI = countOfUIElement - daysInMonth - (adjustedFirstDay - 1)
                    const currentNextMonthDay = (countOfUIElement - increaseIndex - daysInNextMonthInCurrentMonthUI) * -1

                    return (
                        <div key={`next-${currentNextMonthDay}`} className={styles.datePicker__item} onClick={() => setNewDate(currentNextMonthDay, nextMonthIndex, nextMonthYear)}>
                            <div className={`${styles.datePicker__item_elem} ${styles.notCurrentDay}`}>
                                {currentNextMonthDay}
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )

    const renderMonths = () => (
        <div className={styles.datePicker__monthsGrid}>
            {MONTH_NAMES.map((monthName, index) => {
                const isActive = date?.getFullYear() === currentYear && date?.getMonth() === index
                const isNow = now.getFullYear() === currentYear && now.getMonth() === index

                return (
                    <button
                        key={monthName}
                        type="button"
                        className={`${styles.datePicker__gridOption} ${isActive ? styles.active : ''} ${isNow ? styles.now : ''}`}
                        onClick={() => selectMonth(index)}
                    >
                        {monthName}
                    </button>
                )
            })}
        </div>
    )

    const renderYears = () => (
        <div className={styles.datePicker__yearsGrid}>
            {years.map((year) => {
                const isOutsideDecade = year < decadeStart || year > decadeStart + 9
                const isActive = date?.getFullYear() === year
                const isNow = now.getFullYear() === year

                return (
                    <button
                        key={year}
                        type="button"
                        className={`${styles.datePicker__gridOption} ${isActive ? styles.active : ''} ${isNow ? styles.now : ''} ${isOutsideDecade ? styles.notCurrentDay : ''}`}
                        onClick={() => selectYear(year)}
                    >
                        {year}
                    </button>
                )
            })}
        </div>
    )

    return (
        <div className={styles.datePicker}>
            <div className={styles.datePicker__pick}>
                <div className={styles.datePicker__pick_btn} onClick={handlePrev}><ArrowLeftIcon /></div>
                <div className={styles.datePicker__pick_item}>
                    {mode === 'days' && (
                        <>
                            <button type="button" className={styles.datePicker__pick_value} onClick={() => setMode('months')}>
                                {MONTH_NAMES[currentMonth - 1]}
                            </button>
                            <button type="button" className={styles.datePicker__pick_value} onClick={() => setMode('years')}>
                                {currentYear}
                            </button>
                        </>
                    )}
                    {mode === 'months' && (
                        <button type="button" className={styles.datePicker__pick_value} onClick={() => setMode('years')}>
                            {currentYear}
                        </button>
                    )}
                    {mode === 'years' && (
                        <div className={styles.datePicker__pick_valueStatic}>
                            {decadeStart}-{decadeStart + 9}
                        </div>
                    )}
                </div>
                <div className={styles.datePicker__pick_btn} onClick={handleNext}><ArrowRightIcon /></div>
            </div>

            {mode === 'days' && renderDays()}
            {mode === 'months' && renderMonths()}
            {mode === 'years' && renderYears()}
        </div>
    )
}
